import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  CreateManualTransactionInput,
  Transaction,
  TransactionsListResponse,
  TransactionsQuery,
  UpdateTransactionInput,
} from "@money/shared";
import { apiFetch } from "./client";
import { accountsKey } from "./accounts";

export const transactionsKey = (query?: TransactionsQuery) =>
  query ? (["transactions", query] as const) : (["transactions"] as const);

export function useReviewQueue() {
  return useQuery({
    queryKey: ["transactions", "review"] as const,
    queryFn: () => apiFetch<TransactionsListResponse>("/transactions/review"),
    staleTime: 10_000,
  });
}

export function useTransactions(query: TransactionsQuery = {}) {
  return useQuery({
    queryKey: transactionsKey(query),
    queryFn: () =>
      apiFetch<TransactionsListResponse>("/transactions", {
        query: {
          from: query.from,
          to: query.to,
          account: query.account,
          category: query.category,
          reviewed: query.reviewed,
        },
      }),
    staleTime: 30_000,
  });
}

export function useInfiniteTransactions(filters: TransactionsQuery = {}) {
  return useInfiniteQuery({
    queryKey: ["transactions", "infinite", filters] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      apiFetch<TransactionsListResponse>("/transactions", {
        query: {
          from: filters.from,
          to: filters.to,
          account: filters.account,
          category: filters.category,
          reviewed: filters.reviewed,
          limit: filters.limit,
          cursor: pageParam,
        },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 30_000,
  });
}

export function useCreateManualTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateManualTransactionInput) =>
      apiFetch<Transaction>("/transactions/manual", { method: "POST", body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["transactions"] });
      qc.invalidateQueries({ queryKey: accountsKey });
    },
  });
}

export function useUpdateTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; patch: UpdateTransactionInput }) =>
      apiFetch<Transaction>(`/transactions/${input.id}`, {
        method: "PATCH",
        body: input.patch,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
}

export function useDeleteTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/transactions/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["transactions"] });
      qc.invalidateQueries({ queryKey: accountsKey });
    },
  });
}

export function useDeletedTransactions() {
  return useQuery({
    queryKey: ["transactions", "deleted"] as const,
    queryFn: () => apiFetch<TransactionsListResponse>("/transactions/deleted"),
    staleTime: 10_000,
  });
}

export function useRestoreTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<Transaction>(`/transactions/${id}/restore`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["transactions"] });
      qc.invalidateQueries({ queryKey: accountsKey });
    },
  });
}
